// De-duplicates identical read requests and serves them from a short-lived cache.
// Prevents request storms when several views (or several mounts of the same view)
// ask for the same data at once, which was tripping the API rate limiter (429).

const DEFAULT_TTL = 60 * 1000;

const inFlight = new Map();
const cache = new Map();

export const clearRequestCache = () => {
    cache.clear();
    inFlight.clear();
};

export const invalidateRequest = (key) => {
    cache.delete(key);
    inFlight.delete(key);
};

/**
 * @param {string} key    Unique key for the request (endpoint + params).
 * @param {Function} requestFn  Called only when there is no fresh/in-flight result.
 * @param {number} ttl    How long a successful result stays fresh, in ms.
 */
export const cachedRequest = (key, requestFn, ttl = DEFAULT_TTL) => {
    const cached = cache.get(key);
    if (cached && Date.now() - cached.time < ttl) {
        return Promise.resolve(cached.value);
    }

    const pending = inFlight.get(key);
    if (pending) {
        return pending;
    }

    const promise = Promise.resolve()
        .then(requestFn)
        .then((value) => {
            cache.set(key, { time: Date.now(), value });
            inFlight.delete(key);
            return value;
        })
        .catch((error) => {
            // Failures are never cached, so the next caller retries.
            inFlight.delete(key);
            throw error;
        });

    inFlight.set(key, promise);
    return promise;
};

// A new token means a different user context, so nothing cached is valid anymore.
if (typeof window !== 'undefined') {
    window.addEventListener('authComplete', clearRequestCache);
    window.addEventListener('tokenChanged', clearRequestCache);
}
